import React from "react";

import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';

import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';

import { AnomalieType } from '../../../constants';
import { KitType } from '../../../constants';

import MessageDialog from "../MessageDialog";


const LigneEditee = ({
    row,
    reclamation,
    typeAnomalie,
    ligneQualification,
    onChange,
    onChangeQuantiteAnomalie,
    onCancelEditing,
    onDoneEditing,
    onDeleteEditing,
    quantiteLibre,
    afficherQuantiteRetournable,
    capperSaisie }) => {

    // DEV-4347 : le plafonnement de la saisie (capperSaisie) est découplé de l'affichage des colonnes
    // (afficherQuantiteRetournable). Quand capperSaisie est actif (retour soumis à la quantité retournable),
    // la saisie est bornée au retournable — ceinture front, en plus de la garde back. Sinon on borne à la
    // quantité de la ligne (comportement historique).
    const quantiteMax = capperSaisie ? row.quantiteRetournable : row.quantite;
    const libelleQuantiteMax = capperSaisie ? 'quantité retournable' : 'quantité commandée';

    // WARNING : kit 
    let retourKitWarning = typeAnomalie === AnomalieType.DemandeRetourVolontaire && row.kitType === KitType.Compose;
    const [retourKitWarningOpen, setRetourKitWarningOpen] = React.useState(false);

    // WARNING : manque accessoire 
    let manqueAccessoireWarning = typeAnomalie === AnomalieType.DefautProduitManqueAccessoire;
    const [manqueAccessoireWarningOpen, setManqueAccessoireWarningOpen] = React.useState(manqueAccessoireWarning);
    

    // Hook erreur de quantité
    const [errorQuantity, setErrorQuantity] = React.useState({
        error: false,
        message: ''
    });

    // Handler de validation
    const handleClickValid = () => {
        if (retourKitWarning) {
            setRetourKitWarningOpen(true);
        } else {
            onDoneEditing();
        }
    };

    // Handler du onChangeQuantiteAnomalie
    const handleOnChangeQuantiteAnomalie = (event) => {
        onChangeQuantiteAnomalie(event);

        let quantiteAnomalie = parseInt(event.target.value);

        if (!isNaN(quantiteAnomalie)) {

            if (!quantiteLibre && quantiteMax < quantiteAnomalie) {
                setErrorQuantity({ error: true, message: `La quantité retournée est supérieure à la ${libelleQuantiteMax}, modifiez votre quantité` })
            }
            else {
                setErrorQuantity({ error: false, message: '' })
            }
        }
        else if (event.target.value === '') {
            setErrorQuantity({ error: true, message: 'La quantité doit être renseignée' })
        }
    }

    const getTypeAnomalieAffichage = (typeAnomalie) => {
        const lstTypeAnomalieAfficherCodeArticle = [AnomalieType.ErreurProduit];
        const lstTypeAnomalieAfficherDescriptif = [AnomalieType.ErreurProduit, AnomalieType.DefautProduitManqueAccessoire, AnomalieType.DefautProduitCasse, AnomalieType.DefautProduitAspectDeballage, AnomalieType.DefautProduitPremiereUtilisation, AnomalieType.DefautApresFonctionnement];

        let result = {
            libelleQuantite: 'Quantité anomalie',
            libelleCodeArticle: 'Code article anomalie',
            libelleDescriptif: 'Description de l\'anomalie',
            libelleCommentaire: 'Commentaire',
            AfficherCodeArticle: lstTypeAnomalieAfficherCodeArticle.includes(typeAnomalie),
            AfficherDescriptif: lstTypeAnomalieAfficherDescriptif.includes(typeAnomalie)
        };

        switch (typeAnomalie) {
            case AnomalieType.QuantiteArticleIncorrecte:
                result.libelleQuantite = 'Quantité réellement reçue';
                break;
            case AnomalieType.ErreurProduit:
                result.libelleCodeArticle = 'Code article reçu à la place';
                result.libelleQuantite = 'Quantité reçue à la place';
                result.libelleDescriptif = 'Description du produit reçu';
                break
            case AnomalieType.DefautProduitManqueAccessoire:
                result.libelleQuantite = 'Quantité manquante';
                result.libelleDescriptif = 'Description de l\'accessoire manquant';
                break;
            case AnomalieType.DefautProduitAspectDeballage:
                result.libelleQuantite = 'Quantité détériorée';
                result.libelleDescriptif = 'Description de l\'élément défectueux';
                break;
            case AnomalieType.ColisAbime:
                result.libelleQuantite = 'Quantité abîmée';
                break;
            case AnomalieType.DefautProduitPremiereUtilisation:
                result.libelleQuantite = 'Quantité détériorée';
                result.libelleDescriptif = 'Défaut constaté';
                break;
            case AnomalieType.DemandeRetour:
            case AnomalieType.DemandeRetourVolontaire:
                result.libelleQuantite = 'Quantité à retourner';
                break;
            case AnomalieType.ClientPasRetirerMarchandise:
                result.libelleQuantite = 'Quantité reprise';
                break;
            default: {
                break;
            }
        }

        return result;
    };

    const typeAnomalieAffichage = getTypeAnomalieAffichage(typeAnomalie);
    const afficherCodeArticleAnomalie = typeAnomalieAffichage.AfficherCodeArticle;
    const afficherDescriptifAnomalie = typeAnomalieAffichage.AfficherDescriptif;
    let requisDescriptifAnomalie = typeAnomalie === AnomalieType.DefautProduitManqueAccessoire;

    const placeholderCodeArticleAnomalie = typeAnomalieAffichage.libelleCodeArticle;
    const placeholderQuantiteAnomalie = typeAnomalieAffichage.libelleQuantite;
    const placeholderCommentaireAnomalie = 'Commentaire';
    const placeholderDescriptifAnomalie = typeAnomalieAffichage.libelleDescriptif;


    return (
        <React.Fragment>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell component="th" scope="row">Numéro de ligne</TableCell>
                        <TableCell component="th" scope="row">Code article commandé</TableCell>
                        <TableCell component="th" scope="row">Désignation</TableCell>

                        {(typeAnomalie === AnomalieType.DefautProduitPremiereUtilisation) && (
                            <React.Fragment>
                                <TableCell component="th" scope="row">Marque exclusive</TableCell>
                                <TableCell component="th" scope="row">Fournisseur</TableCell>
                            </React.Fragment>
                        )}
                        {(typeAnomalie !== AnomalieType.DefautProduitPremiereUtilisation) && row.fournisseur && (
                            <TableCell component="th" scope="row">Fournisseur</TableCell>
                        )}

                        <TableCell component="th" scope="row" align='right'>Quantité commandée</TableCell>
                        <TableCell component="th" scope="row" align='right'>Prix unitaire</TableCell>

                        {afficherCodeArticleAnomalie && (
                            <TableCell component="th" scope="row">{typeAnomalieAffichage.libelleCodeArticle}</TableCell>
                        )}
                        <TableCell component="th" scope="row" align='right'>{typeAnomalieAffichage.libelleQuantite}</TableCell>
                        {afficherDescriptifAnomalie && (
                            <TableCell component="th" scope="row">{typeAnomalieAffichage.libelleDescriptif}</TableCell>
                        )}
                        <TableCell component="th" scope="row">{typeAnomalieAffichage.libelleCommentaire}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>{row.numeroLigne}</TableCell>
                        <TableCell>{row.codeArticle}</TableCell>
                        <TableCell>{row.designationArticle}</TableCell>

                        {(typeAnomalie === AnomalieType.DefautProduitPremiereUtilisation) && (
                            <React.Fragment>
                                <TableCell>{row.marqueExclusive ? 'Oui' : 'Non'}</TableCell>
                                <TableCell>{row.fournisseur ? <>{row.fournisseur.nom}{row.fournisseur.autoriseSAVPlateformeLogistique ? ' *' : ''}</> : ''}</TableCell>
                            </React.Fragment>
                        )}
                        {(typeAnomalie !== AnomalieType.DefautProduitPremiereUtilisation) && row.fournisseur && (
                            <TableCell>{row.fournisseur ? (row.fournisseur.nom || row.fournisseur.code || '') : ''}</TableCell>
                        )}

                        <TableCell align='right'>{row.quantite}</TableCell>
                        <TableCell align='right'>{row.prixUnitaire.toFixed(2) + ' €'}</TableCell>

                        {afficherCodeArticleAnomalie && (
                            <TableCell>{reclamation.lignes.find(x => x.numeroLigne === row.numeroLigne)?.codeArticleAnomalie}</TableCell>
                        )}
                        <TableCell align='right'>{reclamation.lignes.find(x => x.numeroLigne === row.numeroLigne)?.quantiteAnomalie}</TableCell>
                        {afficherDescriptifAnomalie && (
                            <TableCell>{reclamation.lignes.find(x => x.numeroLigne === row.numeroLigne)?.descriptifAnomalie}</TableCell>
                        )}
                        <TableCell>{reclamation.lignes.find(x => x.numeroLigne === row.numeroLigne)?.commentaireAnomalie}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Stack direction={"row"} spacing={1} alignItems="baseline" m={1}>
                {
                    afficherCodeArticleAnomalie &&
                    <React.Fragment>
                        <TextField
                            id="code-article-anomalie"
                            name="codeArticleAnomalie"
                            label="Code article"
                            placeholder={placeholderCodeArticleAnomalie}
                            value={ligneQualification.codeArticleAnomalie}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                        <Typography variant="subtitle1"> / {row.codeArticle}</Typography>
                    </React.Fragment>
                }

                <TextField
                    id="quantite-anomalie"
                    name="quantiteAnomalie"
                    label={errorQuantity.error ? "Erreur" : "Quantité"}
                    placeholder={placeholderQuantiteAnomalie}
                    value={ligneQualification.quantiteAnomalie}
                    onChange={handleOnChangeQuantiteAnomalie}
                    type="number"
                    inputProps={{ min: 0, max: quantiteMax }}
                    InputLabelProps={{
                        shrink: true
                    }}

                    sx={{ minWidth: '300px' }}
                    error={errorQuantity.error}
                    helperText={errorQuantity.error ? errorQuantity.message : ""}
                />

                <Typography variant="subtitle1">&nbsp;/&nbsp;{quantiteMax} ({libelleQuantiteMax}) - prix unitaire = {row.prixUnitaire.toFixed(2) + ` €`}</Typography>

            </Stack>

            <Stack spacing={1} sx={{ marginRight: '8px', marginLeft: '8px' }}>
                {
                    afficherDescriptifAnomalie &&
                    <TextField
                        multiline
                        fullWidth
                        id="descriptif-anomalie"
                        name="descriptifAnomalie"
                        label="Descriptif"
                        placeholder={placeholderDescriptifAnomalie}
                        maxRows="2"
                        value={ligneQualification.descriptifAnomalie}
                        onChange={onChange}
                        InputLabelProps={{
                            shrink: true
                        }}
                        required={requisDescriptifAnomalie}
                    />
                }
                <TextField
                    multiline
                    fullWidth
                    id="commentaire-anomalie"
                    name="commentaireAnomalie"
                    label="Commentaire"
                    placeholder={placeholderCommentaireAnomalie}
                    maxRows="2"
                    value={ligneQualification.commentaireAnomalie}
                    onChange={onChange}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
            </Stack>

            <Stack direction={"row"} alignItems="center" sx={{ marginRight: '8px', marginLeft: '8px' }}>
                <Tooltip
                    title={
                        errorQuantity.error ? "Erreur de quantité" :
                        ligneQualification.quantiteAnomalie === '' ? "La quantité doit être renseignée" :
                        (requisDescriptifAnomalie && ligneQualification.descriptifAnomalie === '') ? "Veuillez décrire l'accessoire manquant" :
                        "Enregistrer"
                    }
                    placement="top"
                >
                    <span>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleClickValid}
                            disabled={errorQuantity.error || ligneQualification.quantiteAnomalie === '' || (requisDescriptifAnomalie && ligneQualification.descriptifAnomalie === '')}
                            startIcon={<DoneIcon />}
                        />
                    </span>
                </Tooltip>
                <Tooltip title="Fermer" placement="top" >
                    <span>
                        <IconButton aria-label="Fermer" onClick={onCancelEditing} size="large">
                            <ClearIcon color="primary" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Stack sx={{ flexGrow: 1 }} />
                <Tooltip title="Supprimer" placement="top">
                    <span>
                        <IconButton aria-label="Supprimer" onClick={onDeleteEditing} size="large">
                            <DeleteIcon color="primary" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {retourKitWarning && (

                <MessageDialog
                    titre="Attention"
                    contenu="Retour attendu de l'ensemble des composants du kit"
                    ouvert={retourKitWarningOpen}
                    init={true}

                    texteNOK="Retour"
                    texteOK="Valider"

                    onNOK={() => { setRetourKitWarningOpen(false); }}
                    onOK={onDoneEditing}
                />

            )}

            {manqueAccessoireWarning && (

                <MessageDialog
                    titre="Attention"
                    contenu="Merci d'indiquer clairement et précisément l'accessoire manquant et de joindre tous les éléments permettant de l'identifier."
                    ouvert={manqueAccessoireWarningOpen}
                    init={true}

                    onOK={() => { setManqueAccessoireWarningOpen(false); }}
                />

            )}


        </React.Fragment>
    );
}

LigneEditee.propTypes = {
};

LigneEditee.defaultProps = {
};

export default LigneEditee;


